// DESCRIPTION: Step 2 of the event creation wizard — event type as visual
// DESCRIPTION: choice cards, then a two-column row: schedule (availability editor
// DESCRIPTION: or date/time) on the left, location on the right. Full-width cards.
import { __, sprintf } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { TextControl, SelectControl } from '@wordpress/components';
import FieldError from './FieldError';
import DateRangePicker from './DateRangePicker';
import AvailabilityEditor from '../AvailabilityEditor';
import TimeInput from '../TimeInput';

const CARD =
	'nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-p-6 nb-shadow-elevation-1';
const HEADING = 'nb-text-base nb-font-semibold nb-text-neutral-900 nb-mt-0 nb-mb-5';
const LABEL = 'nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-1';

/**
 * Contextual help descriptions for each event type.
 */
const EVENT_TYPE_INFO = {
	'one-on-one': {
		description: __(
			'Private appointments with a single attendee per time slot. Each booking fills the slot completely.',
			'nemtly-booking'
		),
		help: __(
			'Best for: doctor visits, barber appointments, consulting calls, tutoring sessions, therapy, personal training.',
			'nemtly-booking'
		),
		details: [
			__( 'Only 1 person can book each time slot', 'nemtly-booking' ),
			__( 'Slot becomes unavailable after booking', 'nemtly-booking' ),
			__( '15-minute buffer between appointments', 'nemtly-booking' ),
		],
	},
	group: {
		description: __(
			'Sessions with a fixed number of spots. Multiple people book into the same time slot until capacity is reached.',
			'nemtly-booking'
		),
		help: __(
			'Best for: yoga classes, workshops, cooking classes, group training, seminars, study groups.',
			'nemtly-booking'
		),
		details: [
			__(
				'Multiple attendees share the same time slot',
				'nemtly-booking'
			),
			__( 'Bookings close when capacity is reached', 'nemtly-booking' ),
			__( 'Waitlist available when full', 'nemtly-booking' ),
		],
	},
	event: {
		description: __(
			'Large-scale gatherings with high or unlimited capacity. Attendees purchase tickets or RSVP.',
			'nemtly-booking'
		),
		help: __(
			'Best for: concerts, conferences, meetups, festivals, galas, webinars, product launches.',
			'nemtly-booking'
		),
		details: [
			__( 'High or unlimited attendee capacity', 'nemtly-booking' ),
			__( 'Ticket/RSVP-style registration', 'nemtly-booking' ),
			__( 'No per-slot scheduling constraints', 'nemtly-booking' ),
		],
	},
};

const LOCATION_TYPES = {
	venue: { label: __( 'Venue', 'nemtly-booking' ), icon: '📍' },
	virtual: { label: __( 'Virtual', 'nemtly-booking' ), icon: '💻' },
	hybrid: { label: __( 'Hybrid', 'nemtly-booking' ), icon: '🏢' },
};

const DURATION_PRESETS = [
	{ label: __( '15 minutes', 'nemtly-booking' ), value: '15' },
	{ label: __( '30 minutes', 'nemtly-booking' ), value: '30' },
	{ label: __( '45 minutes', 'nemtly-booking' ), value: '45' },
	{ label: __( '60 minutes', 'nemtly-booking' ), value: '60' },
	{ label: __( '90 minutes', 'nemtly-booking' ), value: '90' },
	{ label: __( '120 minutes', 'nemtly-booking' ), value: '120' },
];

const ScheduleLocationStep = ( {
	formData,
	onFieldChange,
	eventTypes = {},
	fieldErrors = {},
} ) => {
	const [ locationType, setLocationType ] = useState(
		formData.virtual_url
			? formData.location
				? 'hybrid'
				: 'virtual'
			: 'venue'
	);

	const selectedType = formData.event_type
		? eventTypes[ formData.event_type ]
		: null;
	const typeInfo = formData.event_type
		? EVENT_TYPE_INFO[ formData.event_type ]
		: null;

	const isSlotBased =
		formData.event_type === 'one-on-one' ||
		formData.event_type === 'group';
	const isEvent = formData.event_type === 'event';

	const handleLocationTypeChange = ( type ) => {
		setLocationType( type );
		if ( type === 'venue' ) {
			onFieldChange( 'virtual_url', '' );
		} else if ( type === 'virtual' ) {
			onFieldChange( 'location', '' );
		}
	};

	return (
		<div className="nemtly-wizard-step-content nb-grid nb-grid-cols-1 nb-gap-6">
			{ /* Event Type — full width */ }
			<div className={ CARD }>
				<h3 className={ HEADING }>
					{ __( 'Event type', 'nemtly-booking' ) }
				</h3>
				<div
					className={ `nb-mb-2 ${
						fieldErrors.event_type ? 'nemtly-field-has-error' : ''
					}` }
				>
					<div
						className="nb-grid nb-gap-3 nb-grid-cols-1 sm:nb-grid-cols-3"
						role="radiogroup"
						aria-label={ __( 'Event type', 'nemtly-booking' ) }
					>
						{ Object.entries( eventTypes ).map( ( [ key, type ] ) => {
							const info = EVENT_TYPE_INFO[ key ];
							const isActive = formData.event_type === key;
							return (
								<button
									key={ key }
									type="button"
									role="radio"
									aria-checked={ isActive }
									onClick={ () =>
										onFieldChange( 'event_type', key )
									}
									className={ `nb-text-left nb-p-4 nb-rounded-lg nb-border nb-cursor-pointer nb-transition-colors nb-bg-neutral-0 ${
										isActive
											? 'nb-border-accent-500 nb-bg-accent-50 nb-shadow-elevation-1'
											: 'nb-border-neutral-200 hover:nb-border-neutral-500'
									}` }
								>
									<div className="nb-flex nb-items-center nb-gap-2 nb-mb-1">
										<span
											className="nb-text-xl"
											aria-hidden="true"
										>
											{ type.icon || '📅' }
										</span>
										<span className="nb-text-sm nb-font-semibold nb-text-neutral-900">
											{ type.label }
										</span>
										{ isActive && (
											<span
												className="nb-ml-auto nb-w-5 nb-h-5 nb-flex nb-items-center nb-justify-center nb-rounded-full nb-bg-accent-500 nb-text-white"
												aria-hidden="true"
											>
												<svg
													className="nb-w-3 nb-h-3"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
														clipRule="evenodd"
													/>
												</svg>
											</span>
										) }
									</div>
									{ info && (
										<p className="nb-text-xs nb-text-neutral-500 nb-m-0 nb-leading-snug">
											{ info.description }
										</p>
									) }
								</button>
							);
						} ) }
					</div>
					<FieldError error={ fieldErrors.event_type } />
				</div>

				{ selectedType && typeInfo && (
					<div className="nb-p-4 nb-bg-neutral-50 nb-border nb-border-neutral-200 nb-rounded-lg">
						<p className="nb-text-sm nb-text-neutral-900 nb-font-medium nb-m-0 nb-mb-1">
							{ selectedType.description || typeInfo.description }
						</p>
						<p className="nb-text-xs nb-text-neutral-500 nb-m-0 nb-mb-3 nb-italic">
							{ selectedType.help || typeInfo.help }
						</p>
						<ul className="nb-m-0 nb-p-0 nb-list-none nb-space-y-1">
							{ typeInfo.details.map( ( detail, i ) => (
								<li
									key={ i }
									className="nb-flex nb-items-start nb-gap-2 nb-text-xs nb-text-neutral-700"
								>
									<span
										className="nb-text-accent-700 nb-mt-px"
										aria-hidden="true"
									>
										&#x2713;
									</span>
									{ detail }
								</li>
							) ) }
						</ul>
					</div>
				) }
			</div>

			{ /* No type selected yet */ }
			{ ! formData.event_type && (
				<div className="nb-p-6 nb-text-center nb-text-neutral-500 nb-text-sm nb-italic">
					{ __(
						'Select an event type above to configure the schedule.',
						'nemtly-booking'
					) }
				</div>
			) }

			{ /* Schedule (left) + Location (right) */ }
			{ formData.event_type && (
				<div className="nb-grid nb-grid-cols-1 lg:nb-grid-cols-3 nb-gap-6 nb-items-start">
					{ /* Left: schedule */ }
					<div className="lg:nb-col-span-2 nb-grid nb-grid-cols-1 nb-gap-6">
						{ isSlotBased && (
							<div className={ CARD }>
								<h3 className={ HEADING }>
									{ __( 'Availability', 'nemtly-booking' ) }
								</h3>

								<div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-5 nb-mb-5">
									<div
										className={
											fieldErrors.event_duration
												? 'nemtly-field-has-error'
												: ''
										}
									>
										<label className={ LABEL }>
											{ __( 'Slot duration', 'nemtly-booking' ) }{ ' ' }
											<span className="nb-text-danger-500">*</span>
										</label>
										<SelectControl
											value={ String(
												formData.event_duration
											) }
											options={ DURATION_PRESETS }
											onChange={ ( value ) =>
												onFieldChange(
													'event_duration',
													parseInt( value )
												)
											}
											__next40pxDefaultSize
											__nextHasNoMarginBottom
										/>
										<FieldError
											error={ fieldErrors.event_duration }
										/>
									</div>
									<div className="nb-flex nb-items-center nb-text-sm nb-text-neutral-500 nb-bg-neutral-50 nb-border nb-border-neutral-200 nb-rounded-md nb-px-3 nb-py-2">
										{ sprintf(
											/* translators: %d: slot duration in minutes */
											__(
												'Clients book in %d-minute slots within the hours you set below.',
												'nemtly-booking'
											),
											formData.event_duration || 60
										) }
									</div>
								</div>

								<div
									className={
										fieldErrors.availability
											? 'nemtly-field-has-error'
											: ''
									}
								>
									<label className={ LABEL }>
										{ __( 'Available hours', 'nemtly-booking' ) }{ ' ' }
										<span className="nb-text-danger-500">*</span>
									</label>
									<AvailabilityEditor
										availability={ formData.availability || {} }
										onChange={ ( value ) =>
											onFieldChange( 'availability', value )
										}
									/>
									<FieldError error={ fieldErrors.availability } />
								</div>
							</div>
						) }

						{ isEvent && (
							<div className={ CARD }>
								<h3 className={ HEADING }>
									{ __( 'Date & time', 'nemtly-booking' ) }
								</h3>

								<div className="nb-mb-5">
									<label className={ LABEL }>
										{ __( 'Start & end date', 'nemtly-booking' ) }{ ' ' }
										<span className="nb-text-danger-500">*</span>
									</label>
									<DateRangePicker
										startDate={ formData.event_date }
										endDate={ formData.event_end_date || '' }
										onStartChange={ ( value ) =>
											onFieldChange( 'event_date', value )
										}
										onEndChange={ ( value ) =>
											onFieldChange( 'event_end_date', value )
										}
										hasError={ !! fieldErrors.event_date }
									/>
									<FieldError error={ fieldErrors.event_date } />
								</div>

								<div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-5">
									<div
										className={
											fieldErrors.event_time
												? 'nemtly-field-has-error'
												: ''
										}
									>
										<label className={ LABEL }>
											{ __( 'Start time', 'nemtly-booking' ) }{ ' ' }
											<span className="nb-text-danger-500">*</span>
										</label>
										<TimeInput
											value={ formData.event_time }
											onChange={ ( value ) =>
												onFieldChange( 'event_time', value )
											}
											ariaLabel={ __(
												'Start time',
												'nemtly-booking'
											) }
										/>
										<FieldError error={ fieldErrors.event_time } />
									</div>
									<div>
										<label className={ LABEL }>
											{ __( 'End time', 'nemtly-booking' ) }{ ' ' }
											<span className="nb-text-danger-500">*</span>
										</label>
										<TimeInput
											value={ formData.event_end_time || '' }
											onChange={ ( value ) =>
												onFieldChange( 'event_end_time', value )
											}
											ariaLabel={ __(
												'End time',
												'nemtly-booking'
											) }
										/>
									</div>
								</div>
							</div>
						) }
					</div>

					{ /* Right: location */ }
					<div className="lg:nb-col-span-1">
						<div className={ CARD }>
							<h3 className={ HEADING }>
								{ __( 'Location', 'nemtly-booking' ) }
							</h3>

							<div
								className="nb-flex nb-gap-1 nb-p-1 nb-mb-5 nb-bg-neutral-50 nb-border nb-border-neutral-200 nb-rounded-lg"
								role="tablist"
								aria-label={ __( 'Location type', 'nemtly-booking' ) }
							>
								{ Object.entries( LOCATION_TYPES ).map(
									( [ key, { label, icon } ] ) => (
										<button
											key={ key }
											type="button"
											role="tab"
											aria-selected={ locationType === key }
											className={ `nb-flex nb-flex-1 nb-items-center nb-justify-center nb-gap-1.5 nb-px-3 nb-py-1.5 nb-text-sm nb-font-semibold nb-rounded-md nb-border-0 nb-cursor-pointer nb-transition-all ${
												locationType === key
													? 'nb-bg-neutral-0 nb-text-neutral-900 nb-shadow-elevation-1'
													: 'nb-bg-transparent nb-text-neutral-500 hover:nb-text-neutral-700'
											}` }
											onClick={ () =>
												handleLocationTypeChange( key )
											}
										>
											<span aria-hidden="true">{ icon }</span>{ ' ' }
											{ label }
										</button>
									)
								) }
							</div>

							<div className="nb-grid nb-grid-cols-1 nb-gap-5">
								{ ( locationType === 'venue' ||
									locationType === 'hybrid' ) && (
									<div>
										<label className={ LABEL }>
											{ __( 'Venue name', 'nemtly-booking' ) }{ ' ' }
											<span className="nb-text-danger-500">*</span>
										</label>
										<TextControl
											value={ formData.location }
											onChange={ ( value ) =>
												onFieldChange( 'location', value )
											}
											placeholder={ __(
												'Event venue name',
												'nemtly-booking'
											) }
											__next40pxDefaultSize
											__nextHasNoMarginBottom
										/>
									</div>
								) }
								{ ( locationType === 'virtual' ||
									locationType === 'hybrid' ) && (
									<div>
										<label className={ LABEL }>
											{ __(
												'Virtual meeting URL',
												'nemtly-booking'
											) }
										</label>
										<TextControl
											value={ formData.virtual_url || '' }
											onChange={ ( value ) =>
												onFieldChange( 'virtual_url', value )
											}
											placeholder={ __(
												'https://zoom.us/j/…',
												'nemtly-booking'
											) }
											type="url"
											__next40pxDefaultSize
											__nextHasNoMarginBottom
										/>
									</div>
								) }
							</div>
						</div>
					</div>
				</div>
			) }
		</div>
	);
};

export default ScheduleLocationStep;
